import {
  AssignmentTurnedIn,
  ChevronRight,
  ExpandLess,
  ExpandMore,
  MarkEmailReadOutlined,
  MarkEmailUnreadOutlined,
  NotificationsNoneOutlined,
  QuizOutlined,
} from "@mui/icons-material";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import { useState } from "react";
import type { Task } from "fhir/r5";

import { isQuestionnaireTask } from "src-next/modules/questionnaire-builder/questionnaire-tasks";
import { isQuestionnaireAnsweredNotificationTask } from "@/utils/taskNotificationUtils";

import {
  formatNotificationDateTime,
  formatNotificationRelativeTime,
  getNotificationDetailText,
  getNotificationStatusLabel,
  getNotificationVisualKind,
  isNotificationUnread,
} from "./notificationFormat";

interface NotificationItemCardProps {
  task: Task;
  accessType?: string | null;
  isUpdating: boolean;
  onToggleReadStatus: (task: Task) => void;
  onAccessMedicalRecord: (task: Task) => void;
  onRespondQuestionnaire: (task: Task) => void;
}

const NOTIFICATION_ICON = {
  questionnaire: QuizOutlined,
  questionnaireAnswered: AssignmentTurnedIn,
  general: NotificationsNoneOutlined,
} as const;

const COLLAPSED_LINE_CLAMP = 2;

function getStatusColor(
  task: Task,
): "success" | "primary" | "warning" | "default" {
  if (task.status === "completed") return "success";
  if (task.status === "in-progress") return "primary";
  if (task.status === "requested") return "warning";
  return "default";
}

function stopEventPropagation(event: React.MouseEvent) {
  event.stopPropagation();
}

export function NotificationItemCard({
  task,
  accessType,
  isUpdating,
  onToggleReadStatus,
  onAccessMedicalRecord,
  onRespondQuestionnaire,
}: NotificationItemCardProps) {
  const theme = useTheme();
  const [expanded, setExpanded] = useState(false);
  const isPatientView = accessType === "patient";
  const isQ = isQuestionnaireTask(task);
  const isUnread = isNotificationUnread(task);
  const visualKind = getNotificationVisualKind(task);
  const Icon = NOTIFICATION_ICON[visualKind];
  const showPatientQuestionnaireOnly =
    isPatientView && isQ && task.status === "requested";
  const showProfessionalActions = !showPatientQuestionnaireOnly && !(isPatientView && isQ);
  const statusLabel = getNotificationStatusLabel(task, accessType);
  const statusColor = getStatusColor(task);
  const dateSource = task.authoredOn || task.meta?.lastUpdated;
  const title = task.description || "Tarefa sem descrição";
  const detail = getNotificationDetailText(task);
  const accentColor =
    visualKind === "questionnaireAnswered"
      ? theme.palette.success.main
      : visualKind === "questionnaire"
        ? theme.palette.primary.main
        : theme.palette.info.main;
  const statusDotColor =
    statusColor === "success"
      ? theme.palette.success.main
      : statusColor === "primary"
        ? theme.palette.primary.main
        : statusColor === "warning"
          ? theme.palette.warning.main
          : theme.palette.text.disabled;

  const toggleCard = () => {
    setExpanded((prev) => !prev);

    if (isUnread && !showPatientQuestionnaireOnly && !isUpdating) {
      onToggleReadStatus(task);
    }
  };

  const handleCardClick = (event: React.MouseEvent<HTMLDivElement>) => {
    toggleCard();

    // Remove mouse focus ring after click (keep keyboard focus intact).
    if (event.detail > 0) {
      event.currentTarget.blur();
    }
  };

  const handleCardKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      toggleCard();
    }
  };

  const textClampSx = expanded
    ? {}
    : {
        display: "-webkit-box",
        WebkitLineClamp: COLLAPSED_LINE_CLAMP,
        WebkitBoxOrient: "vertical" as const,
        overflow: "hidden",
      };

  const baseBg = expanded
    ? alpha(accentColor, 0.07)
    : isUnread
      ? alpha(accentColor, 0.045)
      : theme.palette.background.paper;
  const hoverBg = expanded
    ? alpha(accentColor, 0.09)
    : isUnread
      ? alpha(accentColor, 0.07)
      : alpha(theme.palette.action.hover, 0.1);

  return (
    <Box
      role="button"
      tabIndex={0}
      aria-expanded={expanded}
      onClick={handleCardClick}
      onKeyDown={handleCardKeyDown}
      sx={{
        position: "relative",
        borderRadius: 2,
        border: "1px solid",
        borderColor: expanded
          ? alpha(accentColor, 0.28)
          : isUnread
            ? alpha(accentColor, 0.22)
            : alpha(theme.palette.divider, 0.85),
        bgcolor: baseBg,
        cursor: "pointer",
        outline: "none",
        WebkitTapHighlightColor: "transparent",
        transition:
          "background-color 0.18s ease, border-color 0.18s ease, box-shadow 0.18s ease",
        "&:hover": {
          borderColor: alpha(accentColor, expanded || isUnread ? 0.34 : 0.2),
          bgcolor: hoverBg,
        },
        "&:active": {
          bgcolor: hoverBg,
        },
        "&:focus-visible": {
          borderColor: alpha(accentColor, 0.32),
          boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.18)}`,
        },
      }}
    >
      <Stack
        direction="row"
        spacing={1}
        alignItems="flex-start"
        sx={{ p: 1.25, pr: showProfessionalActions ? 4.5 : 1.25 }}
      >
        <Box sx={{ position: "relative", flexShrink: 0 }}>
          <Box
            sx={{
              width: 34,
              height: 34,
              borderRadius: "10px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: alpha(accentColor, isUnread ? 0.16 : 0.1),
              color: accentColor,
            }}
          >
            <Icon sx={{ fontSize: 17 }} />
          </Box>
          {isUnread ? (
            <Box
              sx={{
                position: "absolute",
                top: -2,
                right: -2,
                width: 8,
                height: 8,
                borderRadius: "50%",
                bgcolor: accentColor,
                border: `2px solid ${theme.palette.background.paper}`,
              }}
            />
          ) : null}
        </Box>

        <Stack spacing={0.5} sx={{ flex: 1, minWidth: 0 }}>
          <Stack
            direction="row"
            alignItems="flex-start"
            justifyContent="space-between"
            spacing={1}
          >
            <Typography
              variant="subtitle2"
              fontWeight={isUnread ? 700 : 600}
              sx={{
                fontSize: "0.8125rem",
                lineHeight: 1.3,
                letterSpacing: "-0.01em",
                color: "text.primary",
                wordBreak: "break-word",
                ...textClampSx,
              }}
            >
              {title}
            </Typography>

            {dateSource ? (
              <Typography
                variant="caption"
                color="text.secondary"
                title={formatNotificationDateTime(dateSource)}
                sx={{
                  flexShrink: 0,
                  whiteSpace: "nowrap",
                  fontSize: "0.625rem",
                  fontWeight: 500,
                  pt: 0.15,
                }}
              >
                {formatNotificationRelativeTime(dateSource)}
              </Typography>
            ) : null}
          </Stack>

          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              lineHeight: 1.35,
              fontSize: "0.75rem",
              wordBreak: "break-word",
              ...textClampSx,
            }}
          >
            {detail}
          </Typography>

          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Box
              sx={{
                width: 6,
                height: 6,
                borderRadius: "50%",
                bgcolor: statusDotColor,
                flexShrink: 0,
              }}
            />
            <Typography
              variant="caption"
              sx={{
                fontSize: "0.6875rem",
                fontWeight: 600,
                color: statusDotColor,
              }}
            >
              {statusLabel}
            </Typography>
            {dateSource ? (
              <>
                <Typography
                  variant="caption"
                  color="text.disabled"
                  sx={{ fontSize: "0.625rem" }}
                >
                  ·
                </Typography>
                <Typography
                  variant="caption"
                  color="text.disabled"
                  sx={{ fontSize: "0.625rem" }}
                >
                  {formatNotificationDateTime(dateSource)}
                </Typography>
              </>
            ) : null}
            <Box sx={{ flex: 1 }} />
            <Typography
              variant="caption"
              color="text.disabled"
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.25,
                fontSize: "0.625rem",
                fontWeight: 600,
              }}
            >
              {expanded ? (
                <>
                  Ver menos
                  <ExpandLess sx={{ fontSize: 14 }} />
                </>
              ) : (
                <>
                  Ver mais
                  <ExpandMore sx={{ fontSize: 14 }} />
                </>
              )}
            </Typography>
          </Stack>

          {isUpdating ? (
            <Stack direction="row" alignItems="center" spacing={0.75} sx={{ pt: 0.25 }}>
              <CircularProgress size={13} />
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: "0.6875rem" }}
              >
                Atualizando...
              </Typography>
            </Stack>
          ) : showPatientQuestionnaireOnly ? (
            <Button
              fullWidth
              size="small"
              variant="contained"
              endIcon={<ChevronRight sx={{ fontSize: 16 }} />}
              onClick={(event) => {
                stopEventPropagation(event);
                onRespondQuestionnaire(task);
              }}
              sx={{
                mt: 0.5,
                textTransform: "none",
                fontWeight: 700,
                fontSize: "0.75rem",
                borderRadius: 1.5,
                minHeight: 30,
                py: 0.35,
                boxShadow: "none",
              }}
            >
              Responder questionário
            </Button>
          ) : showProfessionalActions &&
            isQuestionnaireAnsweredNotificationTask(task) ? (
            <Button
              size="small"
              variant="text"
              color="primary"
              endIcon={<ChevronRight sx={{ fontSize: 15 }} />}
              onClick={(event) => {
                stopEventPropagation(event);
                onAccessMedicalRecord(task);
              }}
              sx={{
                alignSelf: "flex-start",
                mt: 0.15,
                px: 0.5,
                minHeight: 26,
                textTransform: "none",
                fontWeight: 700,
                fontSize: "0.75rem",
              }}
            >
              Acessar prontuário
            </Button>
          ) : null}
        </Stack>
      </Stack>

      {showProfessionalActions && !isUpdating ? (
        <Tooltip
          title={isUnread ? "Marcar como lida" : "Marcar como não lida"}
          placement="left"
        >
          <IconButton
            size="small"
            aria-label={isUnread ? "Marcar como lida" : "Marcar como não lida"}
            onClick={(event) => {
              stopEventPropagation(event);
              onToggleReadStatus(task);
            }}
            sx={{
              position: "absolute",
              top: 6,
              right: 6,
              width: 28,
              height: 28,
              color: isUnread ? "primary.main" : "text.disabled",
              bgcolor: alpha(
                isUnread
                  ? theme.palette.primary.main
                  : theme.palette.action.active,
                isUnread ? 0.08 : 0.04,
              ),
              "&:hover": {
                bgcolor: alpha(
                  isUnread
                    ? theme.palette.primary.main
                    : theme.palette.action.active,
                  0.14,
                ),
              },
            }}
          >
            {isUnread ? (
              <MarkEmailReadOutlined sx={{ fontSize: 16 }} />
            ) : (
              <MarkEmailUnreadOutlined sx={{ fontSize: 16 }} />
            )}
          </IconButton>
        </Tooltip>
      ) : null}
    </Box>
  );
}
